import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Mail, AlertTriangle } from 'lucide-react';

/**
 * S2b landing page — the social login matched a PIK account whose email
 * has never been verified. We CANNOT auto-link (D3). This page issues a
 * neutral prompt that either:
 *   1. Sends a verification email to that address (linking completes only
 *      when the user clicks the link in the email); or
 *   2. Invites the user to sign in with their existing method instead.
 *
 * The backend intentionally does not reveal whether the address exists — we
 * always report generic "if that email matches an account, we sent a link".
 */
export default function VerifyToLinkPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const provider = params.get('provider') || '';
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const providerLabel = provider === 'google' ? 'Google' : provider === 'microsoft' ? 'Microsoft' : provider;

  if (!token || !provider) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center space-y-4" data-testid="verify-to-link-invalid">
          <AlertTriangle size={32} className="mx-auto text-amber-500" />
          <h1 className="font-heading text-xl font-bold">Invalid link session</h1>
          <p className="text-sm text-muted-foreground">This linking session has expired or was opened incorrectly.</p>
          <Button onClick={() => navigate('/login')} className="rounded-full bg-nav-blue text-white">Back to sign in</Button>
        </div>
      </div>
    );
  }

  const resend = async () => {
    setSending(true);
    try {
      await API.post('/auth/oauth/verify-to-link/start', { token });
      setSent(true);
      toast.success('If that email matches an account, we sent a verification link.');
    } catch {
      // Intentionally generic — never leak account existence.
      setSent(true);
      toast.success('If that email matches an account, we sent a verification link.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="max-w-md w-full space-y-5 text-center" data-testid="verify-to-link-page">
        <div className="mx-auto w-12 h-12 rounded-full bg-nav-blue/10 flex items-center justify-center">
          <Mail size={22} className="text-nav-blue" />
        </div>
        <h1 className="font-heading text-2xl font-bold">Verify to link {providerLabel}</h1>
        <p className="text-sm text-muted-foreground">
          To connect your {providerLabel} sign-in to your PeopleiKnow account we need to confirm this email address is yours.
          If it is, we&apos;ll send you a link. Otherwise, sign in with your existing method to link from Settings.
        </p>
        <div className="space-y-2 pt-2">
          <Button
            onClick={resend}
            disabled={sending}
            className="w-full rounded-full bg-nav-blue text-white h-11"
            data-testid="verify-to-link-send-btn"
          >
            {sending ? 'Sending…' : sent ? 'Send another verification email' : 'Email me a verification link'}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/login')}
            className="w-full rounded-full h-11"
            data-testid="verify-to-link-signin-btn"
          >
            Sign in with existing method instead
          </Button>
        </div>
      </div>
    </div>
  );
}
